import {Component, input, OnDestroy} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_ASYNC_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  TouchedChangeEvent,
  ValidationErrors,
  Validator
} from '@angular/forms';
import {Observable, of, Subscription, tap} from 'rxjs';
import {Input, VehicleHardware, VehicleHardwareType} from '../model/plowops-pro-models';
import {delay, filter, map} from 'rxjs/operators';
import {
  InputConnectionConfigurationComponent
} from '../input-connection-configuration/input-connection-configuration.component';
import {MatFormField, MatInput, MatSuffix} from '@angular/material/input';
import {MatOption, MatSelect} from '@angular/material/select';
import {ConfigurationService} from '../../../../../../configuration/configuration.service';
import {ConfigurationModel} from '../../../../../../shared/models/configuration.model';
import { RateUnitPipe } from '../../../../../../shared/formatting/rate-unit.pipe';

@Component({
  selector: 'app-spreader-configuration',
  imports: [
    ReactiveFormsModule,
    InputConnectionConfigurationComponent,
    MatFormField,
    MatInput,
    MatFormField,
    MatSelect,
    MatOption,
    MatSuffix,
    RateUnitPipe,
  ],
  templateUrl: './spreader-configuration.component.html',
  styleUrls: ['../../../../settings-fields.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: SpreaderConfigurationComponent,
    },
    {
      provide: NG_ASYNC_VALIDATORS,
      multi: true,
      useExisting: SpreaderConfigurationComponent,
    },
  ],
})
export class SpreaderConfigurationComponent implements ControlValueAccessor, Validator, OnDestroy {
  label = input<string>('Device');

  availableInputs = input<Input[]>();

  formGroup: FormGroup;

  subscriptions: Subscription[] = [];
  configuration: ConfigurationModel;

  constructor(fb: FormBuilder, configurationService: ConfigurationService) {
    this.formGroup = fb.group({
      connection: fb.control(null),
      isLiquid: fb.control(false),
      rate: fb.control(null),
    });

    this.subscriptions.push(
      configurationService.sharedConfigurationModel.subscribe(value => {
        this.configuration = value;
      })
    );

    this.subscriptions.push(
      this.formGroup.valueChanges.subscribe(value => {
        const { connection, isLiquid, rate } = value || {};

        this.onChangeListener({
          type: isLiquid ? VehicleHardwareType.LIQUID_SPREADER : VehicleHardwareType.GRANULAR_SPREADER,
          connection,
          liquidSpreaderConfiguration: isLiquid ? { rate } : null,
          granularSpreaderConfiguration: !isLiquid ? { rate } : null,
        } as VehicleHardware);
      })
    );

    this.subscriptions.push(
      this.formGroup.events.pipe(filter(e => e instanceof TouchedChangeEvent && e.touched)).subscribe(() => this.onTouchedListener())
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  writeValue(obj: VehicleHardware) {
    const { type, connection, granularSpreaderConfiguration, liquidSpreaderConfiguration } = obj || {};
    this.formGroup.setValue({
      connection: connection || null,
      isLiquid: type === VehicleHardwareType.LIQUID_SPREADER,
      rate: type === VehicleHardwareType.LIQUID_SPREADER ? liquidSpreaderConfiguration?.rate : granularSpreaderConfiguration?.rate || null,
    });
  }

  onChangeListener = _ => {};

  onTouchedListener = () => {};

  onValidatorChange = () => {};

  registerOnChange(fn: any) {
    this.onChangeListener = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouchedListener = fn;
  }

  setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this.formGroup.disable({ emitEvent: false });
    } else {
      this.formGroup.enable({ emitEvent: false });
    }
  }

  registerOnValidatorChange(fn: () => void) {
    this.onValidatorChange = fn;
  }

  validate(control: AbstractControl): Observable<ValidationErrors | null> {
    // workaround for https://github.com/angular/angular/issues/42815
    return of(null).pipe(
      delay(10),
      map(() => (this.formGroup.invalid ? { invalid: true } : null)),
      tap(() => control.parent?.updateValueAndValidity())
    );
  }
}
